
import {Component, Vue, Watch} from "vue-property-decorator";
import {countdownParts, pad2, CountdownParts, renewalProductKeyForPath, RenewalProductKey} from "@/utils/renewalOffer";

const DISMISS_FLAG = 'icw_renewal_bar_dismissed';

/**
 * Son 7 gün kademesi: app-bar'ın hemen altına yapışık, premium geri sayım şeridi.
 * Çalışma alanını daraltmaz (normal akışta, v-main'in üstünde). Tabular sayılarla
 * layout shift olmadan saniye saniye azalır. Kapatılabilir (oturum başına gizlenir),
 * ancak header'daki yenileme butonu erişilebilir kalır.
 */
@Component
export default class RenewalCountdownBar extends Vue {
  loading = false;
  parts: CountdownParts = {gun: 0, saat: 0, dakika: 0, saniye: 0, bitti: false};
  // Reaktif kapatma durumu (ürün anahtarı -> kapatıldı mı). sessionStorage'dan
  // başlatılır; computed cache reaktif kalsın diye burada tutulur.
  dismissedMap: {[key: string]: boolean} = {};
  private intervalId: number | null = null;

  // $route.path reaktif: ürün geçişinde anahtar ve teklif otomatik güncellenir.
  get productKey(): RenewalProductKey {
    return renewalProductKeyForPath(this.$route.path);
  }

  // Kullanıcının bulunduğu ürünün teklifi (İcraProWeb / e-İcraPro ayrı).
  get offer() {
    return this.$store.getters.renewalOfferFor(this.productKey);
  }

  // Kapatma bayrağı ürün-bazlı: birini kapatmak diğerini gizlemez.
  get dismissFlagKey(): string {
    return `${DISMISS_FLAG}_${this.productKey}`;
  }

  get dismissed(): boolean {
    if (this.dismissedMap[this.productKey] !== undefined) {
      return this.dismissedMap[this.productKey];
    }
    try {
      return sessionStorage.getItem(this.dismissFlagKey) === '1';
    } catch (e) {
      return false;
    }
  }

  get eligibleTier(): boolean {
    return !!this.offer?.eligible && this.offer.tier === 'countdown' && !!this.offer.end_date;
  }

  get visible(): boolean {
    return this.eligibleTier && !this.dismissed && !this.parts.bitti;
  }

  get ariaLabel(): string {
    return `%10 indirim için kalan süre: ${this.parts.gun} gün ${this.parts.saat} saat ${this.parts.dakika} dakika`;
  }

  created() {
    this.tick();
    // Saniye hassasiyeti için 1 sn'de bir; sekme arkada olsa da düzeltilir.
    this.intervalId = window.setInterval(this.tick, 1000);
  }

  beforeDestroy() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }
  }

  // Ürün veya teklif değişince aktif ürünün geri sayımına anında geç
  // (interval beklemeden; offer artık $route.path'e bağlı reaktif computed).
  @Watch('offer')
  onOfferChange() {
    this.tick();
  }

  tick() {
    if (this.offer?.end_date) {
      this.parts = countdownParts(this.offer.end_date);
    } else {
      // Aktif üründe teklif yoksa geri sayımı sıfırla (bitti=false, gizli kalır).
      this.parts = {gun: 0, saat: 0, dakika: 0, saniye: 0, bitti: false};
    }
  }

  pad(n: number): string {
    return pad2(n);
  }

  dismiss() {
    // Reaktif map + kalıcı sessionStorage. Vue.set reaktivite için gerekli.
    this.$set(this.dismissedMap, this.productKey, true);
    try {
      sessionStorage.setItem(this.dismissFlagKey, '1');
    } catch (e) { /* yoksay */ }
  }

  async onRenew() {
    this.loading = true;
    try {
      await this.$store.dispatch('startRenewalCheckout', this.productKey);
    } finally {
      this.loading = false;
    }
  }
}
